import { css } from 'aphrodite';
import moment from 'moment-timezone';
import * as React from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { invalidateSurveyByToken, useClientGetEventRegistrationResponseByToken } from '../../../../queries';
import defaultImage from '../../../../web/assets/default_event_image.svg';
import { IEventRegistrationSurvey, IEventSurveyResponse } from '../../../models/Api';
import { EventSurveyConfirmation } from './EventSurveyConfirmation';
import { EventSurveyForm } from './EventSurveyForm';
import { styleSheet } from './styles';

interface IProps {
	survey: IEventRegistrationSurvey;
}

const FALL_BACK_IMAGE = defaultImage;

export enum EventSurveyPaymentStatus {
	PaymentStarted = 'PaymentStarted',
	PaymentPending = 'PaymentPending',
	PaymentSucceeded = 'PaymentSucceeded',
	PaymentFailed = 'PaymentFailed',
}

export const EventSurvey = ({ survey }: IProps) => {
	// Capture the match here to pass down to child
	const match = useRouteMatch<{
		token?: string;
	}>();

	const { params: urlParams = {}, path: matchPath } = match;
	const [loading, setLoading] = React.useState<boolean>(true);
	const [editResponse, setEditResponse] = React.useState<boolean>(false);
	const [paymentCompleted, setPaymentCompleted] = React.useState<boolean>(false);
	const responseRef = React.useRef<IEventSurveyResponse | null>(null);

	const responseByTokenInterval = React.useCallback(
		(data: IEventSurveyResponse) => {
			// Only poll if payment has been completed
			if (!paymentCompleted) {
				return false;
			}

			// Check if all attendees (including guests) are paid
			const attendeePaid = data?.attendee?.isPaid || false;
			const allGuestsPaid = !data?.guests?.length || data.guests.every(guest => guest.isPaid);

			// If all are paid, stop polling
			if (attendeePaid && allGuestsPaid) {
				return false;
			}

			// Continue polling if payment is completed but not all attendees are paid
			if (data?.id && survey?.isPaidEvent && (data?.isAttending === undefined || data?.isAttending)) {
				return 3000;
			}

			return false;
		},
		[paymentCompleted, survey?.isPaidEvent]
	);
	const { data: recordedResponse } = useClientGetEventRegistrationResponseByToken({
		accessToken: urlParams.token,
		retry: false,
		cacheTime: 0,
		refetchInterval: responseByTokenInterval,
	});

	/**
	 * When a event survey link is sent via lev email additional metadata about the user is injected
	 * a partial event survey response is recorded including name, email and phone number but isAttending response is not set
	 * in this case we want to show the user the RSVP form even though a surveyResponse is present
	 */
	const isPartialSurveyResponseFromEmail = recordedResponse?.isAttending === undefined;

	const h = useHistory<{ response?: IEventSurveyResponse; search?: { checkoutStarted?: boolean } }>();

	const successfulPayments = recordedResponse?.payments?.filter(p => p.status === 'PaymentSucceeded');

	const showConfirmationMessage = React.useMemo(() => {
		if (survey?.isPaidEvent && !editResponse && !successfulPayments?.length && recordedResponse?.isAttending) {
			return false;
		}

		return survey && !editResponse && !isPartialSurveyResponseFromEmail && recordedResponse?.id;
	}, [
		successfulPayments,
		survey,
		editResponse,
		recordedResponse?.isAttending,
		recordedResponse?.id,
		isPartialSurveyResponseFromEmail,
	]);

	const onResponseSubmitted = (response: IEventSurveyResponse) => {
		if (editResponse && !survey?.isPaidEvent) {
			setEditResponse(false);
			setLoading(true);
		}
		responseRef.current = response;
		if (response.token && response.token !== urlParams.token) {
			h.replace({
				pathname: matchPath.replace(':token?', encodeURIComponent(response.token)),
				state: {
					response,
				},
			});
		}
	};

	const onPaymentCompleted = React.useCallback(() => {
		setPaymentCompleted(true);
	}, []);

	const switchToEditResponse = () => {
		invalidateSurveyByToken();
		setEditResponse(true);
	};

	const startMoment = moment.tz(survey?.eventInformation?.startTime, survey?.eventInformation?.timeZone);
	const endMoment = survey?.eventInformation?.endTime
		? moment.tz(survey?.eventInformation?.endTime, survey?.eventInformation?.timeZone)
		: null;
	const formattedEndDate = endMoment
		? startMoment.isSame(endMoment, 'day')
			? ` - ${endMoment.format('hh:mm a')}`
			: ` - ${endMoment.format('MMM Do, YYYY, hh:mm a')}`
		: '';
	const timezoneAbbr = moment().tz(survey?.eventInformation?.timeZone)?.format('z');
	const formattedEventDate = `${startMoment.format('MMM Do, YYYY, hh:mm a')}${formattedEndDate} (${timezoneAbbr})`;
	const formattedAmount = React.useMemo(() => {
		if (survey?.attendeeOptions?.costPerAttendee) {
			const dollarAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(survey?.attendeeOptions?.costPerAttendee / 100);
			return dollarAmount;
		}
		return 0;
	}, [survey?.attendeeOptions?.costPerAttendee]);

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.eventDetailsContainer)}>
				<figure className={css(styleSheet.previewImageContainer)}>
					<img className={css(styleSheet.previewImage)} src={survey?.eventInformation?.image?.url || FALL_BACK_IMAGE} />
				</figure>
				<section className={css(styleSheet.details)}>
					<div>
						<h4 className={css(styleSheet.eventDetailsTitle)}>When</h4>
						<p>{formattedEventDate}</p>
					</div>
					<div>
						<h4 className={css(styleSheet.eventDetailsTitle)}>Where</h4>
						<p className={css(styleSheet.locationAreaWrap)}>{survey?.eventInformation?.location}</p>
					</div>
					<div>
						<h4 className={css(styleSheet.eventDetailsTitle)}>Details</h4>
						<pre className={css(styleSheet.detailsAreaWrap)}>{survey?.eventInformation?.details}</pre>
					</div>
					{survey?.isPaidEvent ? (
						<div>
							<h4 className={css(styleSheet.eventDetailsTitle)}>Cost</h4>
							<p className={css(styleSheet.eventPrice)}>{formattedAmount}/ticket</p>
						</div>
					) : null}
				</section>
			</div>

			{showConfirmationMessage ? (
				<EventSurveyConfirmation
					survey={survey}
					surveyResponse={recordedResponse}
					onChangeResponseClick={switchToEditResponse}
				/>
			) : (
				<EventSurveyForm
					survey={survey}
					surveyResponse={recordedResponse}
					onResponseSubmitted={onResponseSubmitted}
					routeMatch={match} // Pass the route match as a prop
					loading={loading}
					setLoading={setLoading}
					cancelEdit={() => setEditResponse(false)}
					isEdit={editResponse}
					onPaymentCompleted={onPaymentCompleted}
					isAttending={responseRef?.current?.isAttending}
				/>
			)}
		</div>
	);
};
